import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import { RowExpansion } from "@design-system/ViewTable/RowExpansion";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { Button } from "@design-system/Button/Button";
import { DocumentCategoryRow } from "../utils";
import styles from "../children/DocumentCategoryItem.module.scss";

export const DocumentsCell = memo(function DocumentsCell({
    row,
    table,
}: CellContext<ViewTableFeatures, DocumentCategoryRow, unknown>) {
    const navigate = useStateAwareNavigate();
    const tableMeta = table.options.meta as
        | { entityIds?: number[] }
        | undefined;
    const entityIds = tableMeta?.entityIds?.length
        ? tableMeta.entityIds
        : undefined;

    const { id, documentsCount, children } = row.original;

    return (
        <RowExpansion row={row}>
            {(isExpanded) => {
                // When collapsed (parent row, not expanded), show group count
                const countToDisplay =
                    !isExpanded && (children?.length ?? 0) > 0
                        ? row.original.groupDocumentsCount
                        : documentsCount;

                const categoryIds = [
                    id,
                    ...(!isExpanded ? (children ?? []).map((c) => c.id) : []),
                ];

                return (
                    <ViewTableCell captureClicks>
                        <Button
                            variant="secondary"
                            size="sm"
                            className={styles.documentsButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate("/documents", {
                                    state: {
                                        documentFilters: {
                                            documentCategoryIds: categoryIds,
                                            entityIds,
                                        },
                                    },
                                });
                            }}
                            disabled={countToDisplay <= 0}
                        >
                            {countToDisplay}
                        </Button>
                    </ViewTableCell>
                );
            }}
        </RowExpansion>
    );
});
